import React from "react";
import {
  Box,
  Typography,
  MenuItem,
  Divider,
  InputAdornment,
  IconButton,
  Chip,
  FormControl,
  RadioGroup,
  FormControlLabel,
  Radio,
  Select,
  CircularProgress,
  Tooltip,
} from "@mui/material";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import RemoveRedEyeOutlinedIcon from "@mui/icons-material/RemoveRedEyeOutlined";
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import CloseIcon from "@mui/icons-material/Close";
import InfoIcon from "@mui/icons-material/Info";
import { useFormik } from "formik";
import { useGeolocation } from "@gis/components/useGeoLocation";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootReducerType } from "@store/store";
import * as Yup from "yup";
import {
  setAcrossChainages,
  setAlongChainages,
  setLegacyCoordinateSite,
  setLegacyCoordinates,
  setLegacyField,
  setLegacyFields,
} from "../redux/slice/legacySlice";
import {
  fetchRowLegacyDetailsThunk,
  fetchLegacySubStagesThunk,
  updateLegacyRowApplicationThunk,
  getAuthorityListThunk,
  getROListThunk,
  getPAListThunk,
} from "../redux/thunk/legacyThunk";
import { StyledTextField } from "@components/CustomInput/customInput";
import { useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import {
  getDeleteSignedURLThunk,
  getFileROWSignedURLThunk,
} from "@modules/stepper/redux/thunk/documentThunk";
import { uploadToS3Thunk } from "@store/thunk/UploadS3Thunk";
import { productionCheck } from "@components/buttonGroupView/utils";
import { genericUploadThunk } from "@modules/provisional-payment/redux/thunk/uploadThunk";
import { deleteFileOnAwsAPI } from "../../../utils/s3Upload";
import {
  getAmenitiesUtility,
  getRowUtilitiesThunk,
  proposedUtilityAlignmentThunk,
} from "@modules/stepper-row/redux/thunk/amenitiesThunk";
import MapPanel from "@modules/stepper-row/components/MapPanel";
import MapSearchInput from "@gis/components/MapSearchInput";
import {
  Chainage,
  Crossing,
} from "@modules/stepper-row/components/Chainage/types";
import ChainageSection from "@modules/stepper-row/components/Chainage/ChainageSection";
import { decrypt } from "../../../utils/encryptionUtils";

const inputSX = {
  "& .MuiOutlinedInput-root": {
    height: 36,
    fontSize: "12px",
    background: "#fff",
    outline: "none",
    borderRadius: "4px",
    "& fieldset": { borderColor: "#cbd5e1" },
    "&:hover fieldset": { borderColor: "#94a3b8" },
    "&.Mui-focused fieldset": { borderColor: "#3a1c73" },
  },

  "& .MuiOutlinedInput-input": {
    padding: "8px 10px",

    // Hide number input controls
    "&::-webkit-outer-spin-button, &::-webkit-inner-spin-button": {
      WebkitAppearance: "none",
      margin: 0,
    },

    "&[type=number]": {
      MozAppearance: "textfield",
    },
  },
};

const labelWidth = 240;
const fieldWidth = 400;

const LegacyAmenityDetailsRow = ({
  initialValues,
  onBack,
  setSubmitHandler,
  setIsStepValid,
}: any) => {
  type LegacySubStage = {
    id: string;
    name: string;
    group: string;
  };

  const dispatch = useDispatch<AppDispatch>();
  const {
    start_latitude,
    start_longitude,
    end_latitude,
    end_longitude,
    start_chainage_km,
    start_chainage_m,
    end_chainage_km,
    end_chainage_m,
    state,
    district,
    pincode,
    licenseExpiry,
    highway_category,
    highway_side,
    national_highway_number,
    khasra_number,
    taluka,
    authority,
    access_id,
    license_type_selection: storedLicenseTypeSelection,
  } = useSelector((state: RootReducerType) => state.accessLegacy);

  const [searchParams] = useSearchParams();
  const urlApplicationId = searchParams.get("row");
  const applicationID = urlApplicationId || access_id;

  const [proposedUtilityAlignmentOptions, setProposedUtilityAlignmentOptions] =
    React.useState<any[]>([]);
  const [utilityTypeOptions, setUtilityTypeOptions] = React.useState<any[]>([]);
  const [amenityOptions, setAmenityOptions] = React.useState<any[]>([]);
  const [uploadedFile, setUploadedFile] = React.useState<any>(null);
  const [circleRateDocument, setCircleRateDocument] = React.useState<any>(null);

  const [authorityOptions, setAuthorityOptions] = React.useState<any[]>([]);
  const [roOptions, setRoOptions] = React.useState<any[]>([]);
  const [piuOptions, setPiuOptions] = React.useState<any[]>([]);
  const [selectedAuthority, setSelectedAuthority] = React.useState<string>("");
  const [selectedRo, setSelectedRo] = React.useState<string>("");
  const [selectedPiu, setSelectedPiu] = React.useState<string[]>([]);
  const [piuSelectOpen, setPiuSelectOpen] = React.useState(false);
  const piuSelectRef = React.useRef<any>(null);
  const [loadingAuthority, setLoadingAuthority] = React.useState(false);
  const [loadingRo, setLoadingRo] = React.useState(false);
  const [loadingPiu, setLoadingPiu] = React.useState(false);
  const [applicationCode, setApplicationCode] = React.useState("");
  const [subStageOptions, setSubStageOptions] = React.useState<
    LegacySubStage[]
  >([]);
  const [licenseTypeSelection, setLicenseTypeSelection] = React.useState<
    "PROVISIONAL_APPROVED" | "PROVISIONAL_UNDER_REVIEW" | "FINAL" | ""
  >("");

  // ✅ Track if this is an edit flow
  const isEditMode = React.useMemo(() => {
    return !!(urlApplicationId || initialValues?.id);
  }, [urlApplicationId, initialValues]);

  const isProvisionalUnderReviewSelection =
    licenseTypeSelection === "PROVISIONAL_UNDER_REVIEW";
  const getLicenseTypeFromSelection = (
    selection:
      | "PROVISIONAL_APPROVED"
      | "PROVISIONAL_UNDER_REVIEW"
      | "FINAL"
      | "",
  ) => {
    if (!selection) return "";
    return selection === "FINAL" ? "FINAL" : "PROVISIONAL";
  };
  const shouldShowLicenseDocumentForType = (licenseType: string) =>
    licenseType === "FINAL" ||
    (licenseType === "PROVISIONAL" && !isProvisionalUnderReviewSelection);

  const upsertOption = (
    currentOptions: Array<{ label: string; value: string }>,
    label: string,
    value: string,
  ) => {
    if (!value) return currentOptions;

    const exists = currentOptions.some(
      (opt) => String(opt.value) === String(value),
    );

    if (exists) return currentOptions;

    return [...currentOptions, { label: label || value, value }];
  };

  const getObjectKey = (path?: string) => {
    if (!path) return undefined;
    return path.replace(/^noc-document\/(row|access)\//, "");
  };

  const fetchProposedUtilityAlignmentOptions = async () => {
    try {
      const response = await dispatch(proposedUtilityAlignmentThunk());
      if (response?.payload?.success) {
        const options = response.payload.data.map((item: any) => ({
          label: item.name,
          value: item.id,
        }));
        setProposedUtilityAlignmentOptions(options);
      }
    } catch (err) {
      console.error("Error fetching proposed utility alignment options", err);
    }
  };

  const fetchUtilityTypes = async () => {
    try {
      const response = await dispatch(getRowUtilitiesThunk());
      if (response?.payload?.success) {
        const options = response.payload.data.map((item: any) => ({
          label: item.name,
          value: item.id,
        }));
        setUtilityTypeOptions(options);
      }
    } catch (err) {
      console.error("Error fetching utility types", err);
    }
  };

  const fetchAmenityOptions = async (categoryId: string | number) => {
    if (!categoryId) {
      setAmenityOptions([]);
      return;
    }

    try {
      const response = await dispatch(getAmenitiesUtility(categoryId));
      if (response?.payload?.success) {
        const options = response.payload.data.map((item: any) => ({
          label: item.amenity_name,
          value: item.amenity_name,
        }));
        setAmenityOptions(options);
      }
    } catch (err) {
      console.error("Error fetching utility category", err);
    }
  };

  const fetchSubStages = React.useCallback(async () => {
    try {
      const response = await dispatch(fetchLegacySubStagesThunk()).unwrap();
      const options = Array.isArray(response?.data)
        ? response.data
        : Array.isArray(response)
          ? response
          : [];
      setSubStageOptions(options);
      return options;
    } catch (error) {
      console.error("Error fetching legacy sub-stages:", error);
      setSubStageOptions([]);
      return [];
    }
  }, [dispatch]);

  const filteredSubStageOptions = React.useMemo(() => {
    if (licenseTypeSelection !== "PROVISIONAL_UNDER_REVIEW") {
      return [];
    }
    return subStageOptions.filter(
      (item) => String(item.group || "").toUpperCase() === "PROCESSING",
    );
  }, [subStageOptions, licenseTypeSelection]);

  const fetchAuthorityList = async () => {
    setLoadingAuthority(true);
    try {
      const response = await dispatch(getAuthorityListThunk());
      const data = Array.isArray(response?.payload)
        ? response.payload
        : response?.payload?.data;
      if (data) {
        const options = data.map((item: any) => ({
          label:
            item.name ||
            item.authority_name ||
            item.office_name ||
            item.label ||
            "",
          value:
            item.id || item.authority_id || item.office_id || item.value || "",
        }));
        setAuthorityOptions(
          options.filter((opt: any) => opt.label && String(opt.value)),
        );
      }
    } catch (err) {
      console.error("Error fetching authority list", err);
    } finally {
      setLoadingAuthority(false);
    }
  };

  const fetchRoList = async (authorityId: string) => {
    if (!authorityId) {
      setRoOptions([]);
      return;
    }

    setLoadingRo(true);
    try {
      const response = await dispatch(getROListThunk(authorityId));
      const data = Array.isArray(response?.payload)
        ? response.payload
        : response?.payload?.data;
      if (data) {
        const options = data.map((item: any) => ({
          label:
            item.office_name ||
            item.ro_office_location ||
            item.name ||
            item.label ||
            "",
          value: item.office_id || item.id || item.ro_id || item.value || "",
        }));
        setRoOptions(
          options.filter((opt: any) => opt.label && String(opt.value)),
        );
      }
    } catch (err) {
      console.error("Error fetching RO list", err);
    } finally {
      setLoadingRo(false);
    }
  };

  const fetchPiuList = async (roId: string) => {
    if (!roId) {
      setPiuOptions([]);
      return;
    }

    setLoadingPiu(true);
    try {
      const response = await dispatch(getPAListThunk(roId));
      const data = Array.isArray(response?.payload)
        ? response.payload
        : Array.isArray(response?.payload?.data)
          ? response.payload.data
          : response?.payload?.data;
      if (data) {
        const options = data.map((item: any) => ({
          label:
            item.office_name ||
            item.piu_office_location ||
            item.name ||
            item.label ||
            "",
          value: item.office_id || item.id || item.piu_id || item.value || "",
        }));
        setPiuOptions(
          options.filter((opt: any) => opt.label && String(opt.value)),
        );
      }
    } catch (err) {
      console.error("Error fetching PIU list", err);
    } finally {
      setLoadingPiu(false);
    }
  };

  const handleAuthorityChange = async (
    authorityId: string,
    preserveSelections = false,
  ) => {
    setSelectedAuthority(authorityId);

    formik.setFieldValue("authority", authorityId);

    if (!authorityId) {
      setRoOptions([]);
      setPiuOptions([]);
      setSelectedRo("");
      setSelectedPiu([]);

      formik.setFieldValue("ro", "");
      formik.setFieldValue("piu_ids", []);

      return;
    }

    await fetchRoList(authorityId);

    if (!preserveSelections) {
      setSelectedRo("");
      setSelectedPiu([]);

      formik.setFieldValue("ro", "");
      formik.setFieldValue("piu_ids", []);
    }
  };

  const handleRoChange = async (roId: string, preservePiu = false) => {
    setSelectedRo(roId);

    formik.setFieldValue("ro", roId);

    if (!roId) {
      setPiuOptions([]);
      setSelectedPiu([]);

      formik.setFieldValue("piu_ids", []);

      return;
    }

    await fetchPiuList(roId);

    if (!preservePiu) {
      setSelectedPiu([]);
      formik.setFieldValue("piu_ids", []);
    }
  };

  const mapLegacyResponse = (data: any) => {
    const toInputDate = (value: any) => {
      if (!value) return "";
      const strValue = String(value);
      return strValue.includes("T") ? strValue.split("T")[0] : strValue;
    };

    const normalizeHighwayCategory = (value: any) => {
      if (!value) return "";
      const normalized = String(value)
        .trim()
        .toLowerCase()
        .replace(/\s+/g, " ");

      const map: Record<string, string> = {
        "2 lane": "2-Lane",
        "2-lane": "2-Lane",
        "4 lane": "4-Lane",
        "4-lane": "4-Lane",
        "6 lane": "6-Lane",
        "6-lane": "6-Lane",
        "8 lane": "8-Lane",
        "8-lane": "8-Lane",
        "il/sl": "IL / SL",
        "il / sl": "IL / SL",
      };

      return map[normalized] || String(value).trim();
    };

    const parseChain = (chain: any) => {
      if (!chain) return ["0", "0"];

      const parts = String(chain).split("+");

      return parts.length === 2 ? [parts[0], parts[1]] : [parts[0] || "0", "0"];
    };

    const [startKm, startM] = parseChain(data.start_chainage);
    const [endKm, endM] = parseChain(data.end_chainage);

    const normalizedLicenseType =
      data.application_phase === "PROCESSING"
        ? "PROVISIONAL"
        : data.application_phase || data.license_type || "";

    if (normalizedLicenseType === "FINAL") {
      const finalUploadPath =
        data.final_license_details?.upload_path ||
        data.final_licence_url ||
        data.final_license_url;

      if (finalUploadPath) {
        setUploadedFile({
          originalFileName:
            data.final_license_details?.file_name ||
            data.final_license_name ||
            getObjectKey(finalUploadPath),
          final_license_url: getObjectKey(finalUploadPath),
          final_license_name:
            data.final_license_details?.file_name ||
            data.final_license_name ||
            getObjectKey(finalUploadPath),
          objectKeyFinalLicense: finalUploadPath,
        });
      }
    } else if (normalizedLicenseType === "PROVISIONAL") {
      const provisionalUploadPath =
        data.provisional_license_details?.upload_path ||
        data.provisional_licence_url ||
        data.provisional_license_url;

      if (provisionalUploadPath) {
        setUploadedFile({
          originalFileName:
            data.provisional_license_details?.file_name ||
            data.provisional_license_name ||
            getObjectKey(provisionalUploadPath),
          provisional_license_url: getObjectKey(provisionalUploadPath),
          provisional_license_name:
            data.provisional_license_details?.file_name ||
            data.provisional_license_name ||
            getObjectKey(provisionalUploadPath),
          objectKeyProvisionalLicense: provisionalUploadPath,
        });
      }
    }

    if (data.circle_rate_document) {
      setCircleRateDocument({
        originalFileName: data.circle_rate_document.document_name,
        fileName: data.circle_rate_document.file_name,
        objectKey: data.circle_rate_document.upload_path,
      });
    }

    return {
      ...data,
      startKm: startKm || "",
      startM: startM || "",
      endKm: endKm || "",
      endM: endM || "",
      highway_category: normalizeHighwayCategory(data.highway_category),
      final_license_issue_date:
        normalizedLicenseType === "PROVISIONAL"
          ? typeof (
            data.provisional_license_issue_date ||
            data.provisional_issued_date ||
            data.provisional_license_details?.provisional_noc_issued_date
          ) === "string"
            ? (
              data.provisional_license_issue_date ||
              data.provisional_issued_date ||
              data.provisional_license_details?.provisional_noc_issued_date
            ).split("T")[0]
            : ""
          : typeof (
            data.final_license_issue_date ||
            data.final_issued_date ||
            data.final_license_details?.final_noc_issued_date
          ) === "string"
            ? (
              data.final_license_issue_date ||
              data.final_issued_date ||
              data.final_license_details?.final_noc_issued_date
            ).split("T")[0]
            : "",
      final_expiry_date:
        normalizedLicenseType === "PROVISIONAL"
          ? toInputDate(
            data.provisional_expiry_date ||
            data.provisional_license_details?.provisional_expiry_date,
          )
          : data.final_license_details?.final_expiry_date ||
            data.final_expiry_date
            ? toInputDate(
              data.final_license_details?.final_expiry_date ||
              data.final_expiry_date,
            )
            : "",
      license_type: normalizedLicenseType,
      utility_type:
        data.utility_type || data.utilityType || data.utility_type_name || "",
      amenity: data.amenity || data.amenityType || data.amenity_name || "",
      category_id: data.category_id || data.utility_type_id || "",
      taluka: data.taluka || "",
      proposed_utility_alignment: data.proposed_utility_alignment || "",
      villages_crossed_count: data.villages_crossed_count || "",
      tehsils_crossed_count: data.tehsils_crossed_count || "",
      villages_crossed_names: data.villages_crossed_names || [],
      tehsils_crossed_names: data.tehsils_crossed_names || [],
      // utilized_nh_land_area: data.utilized_nh_land_area || "",
      circle_rate: data.circle_rate || "",
    };
  };

  const fetchLegacyDetails = async (applicationID: string) => {
    try {
      const res = await dispatch(
        fetchRowLegacyDetailsThunk(applicationID as any),
      ).unwrap();
      const mappedData = mapLegacyResponse(res.data);

      dispatch(
        setAlongChainages(
          (res.data.chainage_Details || []).map((item: any) => ({
            id: item.id,
            startChainageKm: String(item.start_chainage_km ?? ""),
            startChainageM: String(item.start_chainage_m ?? ""),
            endChainageKm: String(item.end_chainage_km ?? ""),
            endChainageM: String(item.end_chainage_m ?? ""),
            side: item.side === "LHS" ? "Left-Hand Side" : "Right-Hand Side",
            chainageLength:
              item.length_km != null ? String(item.length_km) : "",
            cableDetails: item.cable_pipe_details ?? "",
          })),
        ),
      );

      dispatch(
        setAcrossChainages(
          (res.data.crossing_Details || []).map((item: any) => ({
            id: item.id,
            crossingChainageKm: String(item.crossing_chainage_km ?? ""),
            crossingChainageM: String(item.crossing_chainage_m ?? ""),
            chainageLength:
              item.length_km != null ? String(item.length_km) : "",
            cableDetails: item.cable_pipe_details ?? "",
          })),
        ),
      );
      setApplicationCode(String(mappedData?.application_code || ""));

      let decryptedSubstageId = "";
      if (mappedData?.legacy_substage_id) {
        const decrypted = await decrypt(mappedData.legacy_substage_id);
        decryptedSubstageId = decrypted || "";
      }

      // ✅ Determine license type selection based on data
      let licenseSelection:
        | "PROVISIONAL_APPROVED"
        | "PROVISIONAL_UNDER_REVIEW"
        | "FINAL"
        | "" = "";

      if (mappedData?.license_type === "FINAL") {
        licenseSelection = "FINAL";
      } else if (mappedData?.license_type === "PROVISIONAL") {
        // Check if there's a legacy_substage_id to determine if it's under review or already approved
        if (decryptedSubstageId) {
          licenseSelection = "PROVISIONAL_UNDER_REVIEW";
        } else {
          licenseSelection = "PROVISIONAL_APPROVED";
        }
      }

      // ✅ Set the license type selection state
      setLicenseTypeSelection(licenseSelection);

      dispatch(
        setLegacyFields({
          ...mappedData,
          license_type: mappedData?.license_type || "",
          license_type_selection: licenseSelection,
          legacy_substage_id: decryptedSubstageId || "",
          legacy_substage_name: mappedData?.legacy_substage_name || "",
          start_chainage_km: mappedData.startKm || "",
          start_chainage_m: mappedData.startM || "",
          end_chainage_km: mappedData.endKm || "",
          end_chainage_m: mappedData.endM || "",
        }),
      );

      // ✅ Direct Formik Prefill
      formik.setValues({
        utility_type: mappedData.utility_type || "",
        amenity: mappedData.amenity || "",
        category_id: mappedData.category_id || "",
        authority: mappedData.authority || "",
        state: mappedData.state || "",
        district: mappedData.district || "",
        pincode: mappedData.pincode || "",
        highway_category: mappedData.highway_category || "",
        national_highway_number: mappedData.national_highway_number || "",
        highway_side: mappedData.highway_side || "",
        taluka: mappedData.taluka || "",
        proposed_utility_alignment: mappedData.proposed_utility_alignment || "",
        final_license_issue_date: mappedData.final_license_issue_date || "",
        final_expiry_date: mappedData.final_expiry_date || "",
        villages_crossed_count: mappedData.villages_crossed_count || "",
        tehsils_crossed_count: mappedData.tehsils_crossed_count || "",
        villages_crossed_names: mappedData.villages_crossed_names || [],
        tehsils_crossed_names: mappedData.tehsils_crossed_names || [],
        // utilized_nh_land_area: mappedData.utilized_nh_land_area || "",
        circle_rate: mappedData.circle_rate || "",
        startKm: mappedData.startKm || "",
        startM: mappedData.startM || "",
        endKm: mappedData.endKm || "",
        endM: mappedData.endM || "",
        license_type: mappedData?.license_type || "",
        legacy_substage_id: decryptedSubstageId,
      });

      const authorityId = mappedData?.authority_id
        ? String(mappedData.authority_id)
        : "";
      const roId = mappedData?.ro_id ? String(mappedData.ro_id) : "";
      const rawPiuIds = mappedData?.piu_ids ?? mappedData?.piu_id ?? [];

      const piuIds = Array.isArray(rawPiuIds)
        ? rawPiuIds.map((id: any) => String(id))
        : rawPiuIds
          ? [String(rawPiuIds)]
          : [];
      const authorityLabel = mappedData?.authority_name
        ? String(mappedData.authority_name)
        : authorityId;
      const roLabel = mappedData?.ro_office_location
        ? String(mappedData.ro_office_location)
        : roId;
      const piuLabels = Array.isArray(mappedData?.piu_office_location)
        ? mappedData.piu_office_location.map((name: any) => String(name).trim())
        : [];

      if (authorityId) {
        setAuthorityOptions((prev) =>
          upsertOption(prev, authorityLabel, authorityId),
        );
      }

      if (authorityId) {
        await handleAuthorityChange(authorityId, true);
      }

      if (roId) {
        setRoOptions((prev) => upsertOption(prev, roLabel, roId));
      }

      if (roId) {
        await handleRoChange(roId, true);
      }

      if (piuIds.length > 0) {
        setPiuOptions((prev) => {
          let next = [...prev];
          piuIds.forEach((id: string, index: number) => {
            next = upsertOption(next, piuLabels[index] || id, id);
          });
          return next;
        });
        setSelectedPiu(piuIds);
        formik.setFieldValue("piu_ids", piuIds);
      }

      return mappedData;
    } catch (error) {
      console.error("Error fetching legacy details:", error);
    }
  };

  const updateLegacyApplication = async () => {
    try {
      const isProvisional = formik.values.license_type === "PROVISIONAL";
      const isFinal = formik.values.license_type === "FINAL";

      const chainage_Details = alongChainages.map((item, index) => ({
        start_chainage_km: Number(item.startChainageKm),
        start_chainage_m: Number(item.startChainageM),
        end_chainage_km: Number(item.endChainageKm),
        end_chainage_m: Number(item.endChainageM),
        side: item.side === "Left-Hand Side" ? "LHS" : "RHS",
        length_km: Number(item.chainageLength),
        cable_pipe_details: item.cableDetails,
        order: index + 1,
      }));

      const crossing_Details = acrossChainages.map((item, index) => ({
        crossing_chainage_km: Number(item.crossingChainageKm),
        crossing_chainage_m: Number(item.crossingChainageM),
        length_km: Number(item.chainageLength),
        cable_pipe_details: item.cableDetails,
        order: chainage_Details.length + index + 1,
      }));

      const response = await dispatch(
        updateLegacyRowApplicationThunk({
          application_id: applicationID,
          start_chainage: `${formik?.values?.startKm}+${formik?.values?.startM}`,
          end_chainage: `${formik?.values?.endKm}+${formik?.values?.endM}`,
          start_latitude: start_latitude,
          start_longitude: start_longitude,
          end_latitude: end_latitude,
          end_longitude: end_longitude,
          authority: formik.values.authority,
          state: formik.values.state,
          district: formik.values.district,
          pincode: Number(formik.values.pincode),
          highway_category: formik.values.highway_category,
          national_highway_number: formik.values.national_highway_number,
          highway_side: formik.values.highway_side,
          utility_type: formik.values.utility_type,
          amenity: formik.values.amenity,
          taluka: formik.values.taluka,
          villages_crossed_count: Number(formik.values.villages_crossed_count),
          tehsils_crossed_count: Number(formik.values.tehsils_crossed_count),
          villages_crossed_names: formik.values.villages_crossed_names,
          tehsils_crossed_names: formik.values.tehsils_crossed_names,
          circle_rate: Number(formik.values.circle_rate),
          // utilized_nh_land_area: Number(formik.values.utilized_nh_land_area),
          proposed_utility_alignment: formik.values.proposed_utility_alignment,
          provisional_license_url: isProvisional
            ? uploadedFile?.provisional_license_url || uploadedFile?.fileName
            : null,
          provisional_license_name: isProvisional
            ? uploadedFile?.provisional_license_name ||
            uploadedFile?.originalFileName
            : null,
          objectKeyProvisionalLicense: isProvisional
            ? uploadedFile?.objectKeyProvisionalLicense
            : null,
          provisional_license_issue_date:
            isProvisional && formik?.values?.final_license_issue_date
              ? new Date(formik.values.final_license_issue_date).toISOString()
              : null,
          provisional_expiry_date:
            isProvisional && formik?.values?.final_expiry_date
              ? new Date(formik.values.final_expiry_date).toISOString()
              : null,
          final_license_url: isFinal
            ? uploadedFile?.final_license_url || uploadedFile?.fileName
            : null,
          final_license_name: isFinal
            ? uploadedFile?.final_license_name || uploadedFile?.originalFileName
            : null,
          objectKeyFinalLicense: isFinal
            ? uploadedFile?.objectKeyFinalLicense
            : null,
          final_license_issue_date:
            isFinal && formik?.values?.final_license_issue_date
              ? new Date(formik.values.final_license_issue_date).toISOString()
              : null,
          final_expiry_date:
            isFinal && formik?.values?.final_expiry_date
              ? new Date(formik.values.final_expiry_date).toISOString()
              : null,
          circle_rate_document: circleRateDocument ? [circleRateDocument] : [],
          authority_id: selectedAuthority || null,
          ro_id: selectedRo || null,
          piu_ids: selectedPiu.length > 0 ? selectedPiu : undefined,
          application_phase: formik.values.license_type,
          legacy_substage_id: formik.values.legacy_substage_id || null,
          ...(isAlong && {
            chainage_Details,
          }),
          ...(isAcross && {
            crossing_Details,
          }),
        }),
      ).unwrap();

      if (response?.success === false) {
        toast.error(
          response?.message || "Failed to update old row application.",
        );
        return false;
      }

      return true;
    } catch (error) {
      console.error("Update Legacy Access Application Failed:", error);
      const errorMessage =
        (error as any)?.message ||
        (error as any)?.response?.data?.message ||
        "Failed to update old row application.";

      toast.error(errorMessage);
      return false;
    }
  };

  const validationSchema = Yup.object().shape({
    state: Yup.string()
      .required("State is required")
      .matches(/^[a-zA-Z\s]+$/, "State should not contain numbers"),
    district: Yup.string()
      .required("District is required")
      .matches(/^[a-zA-Z\s]+$/, "District should not contain numbers"),
    pincode: Yup.string()
      .required("Pincode is required")
      .matches(/^\d{6}$/, "Pincode must be a 6-digit number"),
    highway_category: Yup.string().required("Highway Category is required"),
    national_highway_number: Yup.string().required(
      "National Highway Number is required",
    ),
    highway_side: Yup.string().required("Highway Side is required"),

    utility_type: Yup.string().required("Type of Utility is required"),
    amenity: Yup.string().required("Utility Category is required"),
    taluka: Yup.string()
      .matches(/^[a-zA-Z0-9]*$/, "Only letters and numbers are allowed")
      .required("Taluka is required"),
    license_type: Yup.string()
      .oneOf(["PROVISIONAL", "FINAL"], "Application stage is required")
      .required("Application stage is required"),
    legacy_substage_id: Yup.string().when("license_type", {
      is: (value: string) => {
        if (!value) return false;
        return licenseTypeSelection === "PROVISIONAL_UNDER_REVIEW";
      },
      then: (schema) => schema.required("Please select sub-stage"),
      otherwise: (schema) => schema.notRequired(),
    }),
    proposed_utility_alignment: Yup.mixed().required(
      "Proposed Utility Alignment is required",
    ),
    final_license_issue_date: Yup.string().when(
      "license_type",
      ([license_type], schema) => {
        return shouldShowLicenseDocumentForType(String(license_type || ""))
          ? schema.required("License Issue Date is required")
          : schema.nullable().notRequired();
      },
    ),
    final_expiry_date: Yup.string().when(
      "license_type",
      ([license_type], schema) => {
        return shouldShowLicenseDocumentForType(String(license_type || ""))
          ? schema.required("Expiry Date is required")
          : schema.nullable().notRequired();
      },
    ),
    villages_crossed_count: Yup.string()
      .required("Village count is required")
      .test("is-positive", "At least 1 village required", function (value) {
        return value ? Number(value) > 0 : false;
      }),
    tehsils_crossed_count: Yup.string()
      .required("Tehsil count is required")
      .test("is-positive", "At least 1 tehsil required", function (value) {
        return value ? Number(value) > 0 : false;
      }),
    villages_crossed_names: Yup.array().test(
      "match-village-count",
      "Village names must match the count",
      function (value) {
        const count = Number(this.parent.villages_crossed_count) || 0;
        if (count === 0) return true;
        return (value?.length || 0) === count;
      },
    ),
    tehsils_crossed_names: Yup.array().test(
      "match-tehsil-count",
      "Tehsil names must match the count",
      function (value) {
        const count = Number(this.parent.tehsils_crossed_count) || 0;
        if (count === 0) return true;
        return (value?.length || 0) === count;
      },
    ),
    // utilized_nh_land_area: Yup.number().required("NH Land Area is required"),
    circle_rate: Yup.string()
      .required("Circle Rate is required")
      .test("is-number", "Must be a valid number", function (value) {
        return value ? !isNaN(Number(value)) : false;
      }),
    startKm: Yup.string().required("Start Chainage Km is required"),
    startM: Yup.string().required("Start Chainage m is required"),
    endKm: Yup.string().required("End Chainage Km is required"),
    endM: Yup.string().required("End Chainage m is required"),
    authority: Yup.string().required("Authority is required"),

    ro: Yup.string().required("Regional Office is required"),

    piu_ids: Yup.array()
      .of(Yup.string())
      .min(1, "Please select at least one PIU")
      .required("PIU is required"),
  });

  const formik = useFormik({
    validationSchema,

    validateOnMount: true,
    validateOnChange: true,
    validateOnBlur: true,

    initialValues: {
      utility_type: "",
      amenity: "",
      category_id: "",
      authority: authority || "",
      state: state || "",
      district: district || "",
      pincode: pincode || "",
      highway_category: highway_category || "",
      national_highway_number: national_highway_number || "",
      highway_side: highway_side || "",
      ro_id: "",
      piu_ids: [],
      authority_id: "",
      taluka: "",
      proposed_utility_alignment: "",
      final_license_issue_date: "",
      final_expiry_date: "",
      villages_crossed_count: "",
      tehsils_crossed_count: "",
      villages_crossed_names: [] as string[],
      tehsils_crossed_names: [] as string[],
      // utilized_nh_land_area: "",
      circle_rate: "",
      startKm: "",
      startM: "",
      endKm: "",
      endM: "",
      license_type: "",
      legacy_substage_id: "",
      ...(initialValues || {}),
    },

    onSubmit: async () => {
      try {
        await updateLegacyApplication();
      } catch (error) {
        console.error("Submit failed:", error);
      }
    },
  });

  const effectiveLicenseType =
    formik.values.license_type ||
    getLicenseTypeFromSelection(licenseTypeSelection);

  const { coordinateSite } = useSelector(
    (state: RootReducerType) => state.accessLegacy,
  );

  const alongChainages = useSelector(
    (state: RootReducerType) => state.accessLegacy.along_chainage_details,
  );

  const acrossChainages = useSelector(
    (state: RootReducerType) => state.accessLegacy.crossing_details,
  );

  const { coordinates: geoCoordinates } = useGeolocation({
    enableHighAccuracy: true,
    timeout: 5000,
    maximumAge: 0,
  });

  const start_coordinates = React.useMemo(
    () => ({
      latitude: start_latitude ?? 0,
      longitude: start_longitude ?? 0,
    }),
    [start_latitude, start_longitude],
  );

  const end_coordinates = React.useMemo(
    () => ({
      latitude: end_latitude ?? 0,
      longitude: end_longitude ?? 0,
    }),
    [end_latitude, end_longitude],
  );

  const hasValue = (value: unknown): boolean =>
    value !== null && value !== undefined && value !== "";

  const hasValidCoordinate = (value: unknown): boolean => {
    if (!hasValue(value)) return false;

    const num = Number(value);

    return Number.isFinite(num) && num !== 0;
  };

  const hasRequiredChainages = (): boolean => {
    if (isAlong && (alongChainages?.length ?? 0) === 0) {
      return false;
    }

    if (isAcross && (acrossChainages?.length ?? 0) === 0) {
      return false;
    }

    return true;
  };

  const hasRequiredAuthorityDetails = (): boolean => {
    return (
      Boolean(selectedAuthority) &&
      Boolean(selectedRo) &&
      selectedPiu.length > 0
    );
  };

  const hasRequiredDocuments = (): boolean => {
    const isCircleRateUploaded = Boolean(circleRateDocument);

    const isLicenseDocumentRequired =
      shouldShowLicenseDocumentForType(effectiveLicenseType);

    const isLicenseUploaded = isLicenseDocumentRequired
      ? Boolean(uploadedFile)
      : true;

    return isCircleRateUploaded && isLicenseUploaded;
  };

  const hasSelectedMapCoordinates = (): boolean => {
    return (
      hasValidCoordinate(start_latitude) &&
      hasValidCoordinate(start_longitude) &&
      hasValidCoordinate(end_latitude) &&
      hasValidCoordinate(end_longitude)
    );
  };

  const handleMapClick = React.useCallback(
    (coords: any, chainage: any) => {
      const latitude = Number(coords?.latitude);
      const longitude = Number(coords?.longitude);

      if (
        !Number.isFinite(latitude) ||
        !Number.isFinite(longitude) ||
        latitude === 0 ||
        longitude === 0
      ) {
        return;
      }

      const chainageParts = chainage?.split("+") || ["0", "0"];
      const kmStr = chainageParts[0] || "0";
      const mStr = chainageParts[1] || "0";

      dispatch(
        setLegacyCoordinates({
          site: coordinateSite,
          latitude,
          longitude,
          km: Number(kmStr),
          m: Number(mStr),
        }),
      );

      formik.setFieldValue(`${coordinateSite}_latitude`, latitude);
      formik.setFieldValue(`${coordinateSite}_longitude`, longitude);
      formik.setFieldValue(`${coordinateSite}_chainage_km`, kmStr);
      formik.setFieldValue(`${coordinateSite}_chainage_m`, mStr);

      if (coordinateSite === "start") {
        dispatch(setLegacyCoordinateSite("end"));
      }
    },
    [dispatch, coordinateSite, formik],
  );

  const selectedAlignment = formik.values.proposed_utility_alignment;

  const isAlong = selectedAlignment === 2 || selectedAlignment === 3;

  const isAcross = selectedAlignment === 1 || selectedAlignment === 3;

  const isExpired =
    formik.values.final_expiry_date &&
    new Date(formik.values.final_expiry_date) <
    new Date(new Date().toDateString());

  // ✅ Initialize form - handles both new and edit flows
  React.useEffect(() => {
    const initializeLegacyForm = async () => {
      dispatch(setLegacyCoordinateSite("start"));

      // If in edit mode, fetch and populate data
      if (isEditMode && applicationID) {
        const [legacy, stages] = await Promise.all([
          fetchLegacyDetails(applicationID as any),
          fetchSubStages(),
        ]);

        if (!legacy) return;

        // Handle sub-stage for PROVISIONAL_UNDER_REVIEW
        if (legacy.legacy_substage_id) {
          const decryptedLegacy = await decrypt(legacy.legacy_substage_id);
          const filtered = (stages as any[]).filter(
            (x: any) => String(x.group || "").toUpperCase() === "PROCESSING",
          );

          const decryptedStages = await Promise.all(
            filtered.map(async (item: any) => ({
              item,
              decryptedId: await decrypt(item.id),
            })),
          );

          const matchedStage = decryptedStages.find(
            (x) => x.decryptedId === decryptedLegacy,
          )?.item;

          if (matchedStage) {
            formik.setFieldValue("legacy_substage_id", matchedStage.id, false);
            dispatch(
              setLegacyFields({
                legacy_substage_id: matchedStage.id,
                legacy_substage_name: matchedStage.name,
              }),
            );
          }
        }
      } else {
        // New application - ensure everything is empty
        setLicenseTypeSelection("");
        formik.setFieldValue("license_type", "", false);
        formik.setFieldValue("legacy_substage_id", "", false);
        dispatch(
          setLegacyFields({
            license_type: "",
            license_type_selection: "",
            legacy_substage_id: "",
            legacy_substage_name: "",
          }),
        );
      }
    };

    initializeLegacyForm();
    fetchProposedUtilityAlignmentOptions();
    fetchUtilityTypes();
    fetchAuthorityList();
  }, [applicationID, dispatch, fetchSubStages, isEditMode]);

  React.useEffect(() => {
    if (formik.values.category_id) {
      fetchAmenityOptions(formik.values.category_id);
    }
  }, [formik.values.category_id]);

  React.useEffect(() => {
    if (
      !formik.values.utility_type &&
      formik.values.category_id &&
      utilityTypeOptions.length
    ) {
      const selectedOption = utilityTypeOptions.find(
        (option) => String(option.value) === String(formik.values.category_id),
      );

      if (selectedOption?.label) {
        formik.setFieldValue("utility_type", selectedOption.label);
        dispatch(
          setLegacyField({
            name: "utility_type",
            value: selectedOption.label,
          }),
        );
      }
    }
  }, [
    formik.values.utility_type,
    formik.values.category_id,
    utilityTypeOptions,
    dispatch,
  ]);

  React.useEffect(() => {
    dispatch(
      setLegacyFields({
        start_chainage_km: formik.values.startKm || "",
        start_chainage_m: formik.values.startM || "",
        end_chainage_km: formik.values.endKm || "",
        end_chainage_m: formik.values.endM || "",
      }),
    );
  }, [
    formik.values.startKm,
    formik.values.startM,
    formik.values.endKm,
    formik.values.endM,
    dispatch,
  ]);

  React.useEffect(() => {
    const alignmentLabel =
      proposedUtilityAlignmentOptions.find(
        (option) =>
          String(option.value) ===
          String(formik.values.proposed_utility_alignment),
      )?.label ||
      formik.values.proposed_utility_alignment ||
      "";

    dispatch(
      setLegacyFields({
        license_type: formik.values.license_type || "",
        license_type_selection: licenseTypeSelection,
        legacy_substage_id: formik.values.legacy_substage_id || "",
        legacy_substage_name:
          filteredSubStageOptions.find(
            (item) =>
              String(item.id) === String(formik.values.legacy_substage_id),
          )?.name || "",
        proposed_utility_alignment: alignmentLabel,
        utility_type: formik.values.utility_type || "",
        amenity: formik.values.amenity || "",
        // utilized_nh_land_area: formik.values.utilized_nh_land_area || "",
        taluka: formik.values.taluka || "",
      }),
    );
  }, [
    formik.values.license_type,
    formik.values.legacy_substage_id,
    formik.values.proposed_utility_alignment,
    formik.values.utility_type,
    formik.values.amenity,
    // formik.values.utilized_nh_land_area,
    formik.values.taluka,
    filteredSubStageOptions,
    proposedUtilityAlignmentOptions,
    dispatch,
  ]);

  React.useEffect(() => {
    const issuedDate = formik.values.final_license_issue_date;

    if (!issuedDate) {
      if (formik.values.final_expiry_date !== "") {
        formik.setFieldValue("final_expiry_date", "", false);
      }
      return;
    }

    const issued = new Date(issuedDate);
    const expiry = new Date(issued);

    if (formik.values.license_type === "FINAL") {
      expiry.setFullYear(expiry.getFullYear() + 5);
    } else {
      expiry.setFullYear(expiry.getFullYear() + 1);
    }

    expiry.setDate(expiry.getDate() - 1);

    const expiryDate = expiry.toISOString().split("T")[0];

    if (formik.values.final_expiry_date !== expiryDate) {
      formik.setFieldValue("final_expiry_date", expiryDate, false);
    }
  }, [formik.values.final_license_issue_date, formik.values.license_type]);

  const handleStepSubmit = React.useCallback(async () => {
    const errors = await formik.validateForm(formik.values);

    const touchedFields = Object.keys(formik.values).reduce(
      (acc, key) => {
        acc[key] = true;
        return acc;
      },
      {} as Record<string, boolean>,
    );

    await formik.setTouched(touchedFields, true);

    const hasValidationErrors = Object.keys(errors).length > 0;
    const hasMissingCoordinates = !hasSelectedMapCoordinates();
    const hasMissingDocuments = !hasRequiredDocuments();
    const hasMissingChainages = !hasRequiredChainages();
    const hasMissingAuthorityDetails = !hasRequiredAuthorityDetails();

    if (
      hasValidationErrors ||
      hasMissingCoordinates ||
      hasMissingDocuments ||
      hasMissingChainages ||
      hasMissingAuthorityDetails
    ) {
      return false;
    }

    const isUpdated = await updateLegacyApplication();

    return isUpdated === true;
  }, [
    formik.values,
    formik.validateForm,
    formik.setTouched,
    updateLegacyApplication,
    isAlong,
    isAcross,
    alongChainages,
    acrossChainages,
    circleRateDocument,
    uploadedFile,
    effectiveLicenseType,
    start_latitude,
    start_longitude,
    end_latitude,
    end_longitude,
    selectedAuthority,
    selectedRo,
    selectedPiu,
  ]);

  React.useEffect(() => {
    const isNewApplication = !urlApplicationId || !initialValues?.id;

    if (isNewApplication) {
      dispatch(
        setLegacyCoordinates({
          site: "start",
          latitude: 0,
          longitude: 0,
          km: 0,
          m: 0,
        }),
      );

      dispatch(
        setLegacyCoordinates({
          site: "end",
          latitude: 0,
          longitude: 0,
          km: 0,
          m: 0,
        }),
      );

      dispatch(setLegacyCoordinateSite("start"));

      formik.setFieldValue("startKm", "");
      formik.setFieldValue("startM", "");
      formik.setFieldValue("endKm", "");
      formik.setFieldValue("endM", "");
    }
  }, []);

  React.useEffect(() => {
    setSubmitHandler?.(() => handleStepSubmit);
  }, [setSubmitHandler, formik.values]);

  React.useEffect(() => {
    dispatch(
      setLegacyField({
        name: "license_type",
        value: formik.values.license_type || "",
      }),
    );
  }, [formik.values.license_type, dispatch]);

  React.useEffect(() => {
    let isMounted = true;

    const checkStepValidity = async () => {
      const errors = await formik.validateForm(formik.values);

      if (!isMounted) return;

      const hasValidationErrors = Object.keys(errors).length > 0;
      const hasMissingCoordinates = !hasSelectedMapCoordinates();
      const hasMissingDocuments = !hasRequiredDocuments();
      const hasMissingChainages = !hasRequiredChainages();
      const hasMissingAuthorityDetails = !hasRequiredAuthorityDetails();

      const isStepValid =
        !hasValidationErrors &&
        !hasMissingCoordinates &&
        !hasMissingDocuments &&
        !hasMissingChainages &&
        !hasMissingAuthorityDetails;

      setIsStepValid?.(isStepValid);
    };

    checkStepValidity();

    return () => {
      isMounted = false;
    };
  }, [
    formik.values,
    formik.validateForm,
    setIsStepValid,
    start_latitude,
    start_longitude,
    end_latitude,
    end_longitude,
    circleRateDocument,
    uploadedFile,
    effectiveLicenseType,
    alongChainages,
    acrossChainages,
    isAlong,
    isAcross,
    selectedAuthority,
    selectedRo,
    selectedPiu,
  ]);

  return (
    <>
      <form onSubmit={formik.handleSubmit}>
        <Box
          sx={{
            background: "#fff",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Box sx={{ p: 2.5 }}>
            <Box
              sx={{
                bgcolor: "#FFEECC",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 1,
                mb: 1,
                py: 1,
                px: 2,
                borderRadius: 1,
              }}
            >
              <Typography
                sx={{ px: 1, whiteSpace: "nowrap", textAlign: "center" }}
                fontSize={12}
                fontWeight={500}
              >
                Dear Applicant , This is legacy application{" "}
                <strong>({applicationCode})</strong>, you need to update the
                missing/required information to process your application through
                the <strong>Rajmarg Pravesh Portal</strong>.
              </Typography>
            </Box>
            <Typography fontSize={13} fontWeight={600} mb={1}>
              Amenity Details
            </Typography>
            <Divider sx={{ mb: 2 }} />

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: `${labelWidth}px ${fieldWidth}px`,
                rowGap: "12px",
                columnGap: "32px",
                alignItems: "center",
                fontSize: "12px",
              }}
            >
              <Typography className="gray-700 font-medium">
                State <span className="required">*</span>
              </Typography>
              <StyledTextField
                name="state"
                size="small"
                placeholder="Enter State"
                sx={{ width: `${fieldWidth}px`, ...inputSX }}
                value={formik.values.state || ""}
                onBlur={formik.handleBlur}
                error={formik.touched.state && Boolean(formik.errors.state)}
                helperText={
                  formik.touched.state ? String(formik.errors.state || "") : ""
                }
                onKeyDown={(e) => {
                  if (!isNaN(Number(e.key)) && e.key !== " ") {
                    e.preventDefault();
                  }
                }}
                onChange={(e) => {
                  const sanitized = e.target.value.replace(/[0-9]/g, "");
                  formik.setFieldValue("state", sanitized);
                  dispatch(setLegacyField({ name: "state", value: sanitized }));
                }}
              />

              <Typography className="gray-700 font-medium">
                District <span className="required">*</span>
              </Typography>
              <StyledTextField
                name="district"
                size="small"
                placeholder="Enter District"
                sx={{ width: `${fieldWidth}px`, ...inputSX }}
                value={formik.values.district || ""}
                onBlur={formik.handleBlur}
                error={
                  formik.touched.district && Boolean(formik.errors.district)
                }
                helperText={
                  formik.touched.district
                    ? String(formik.errors.district || "")
                    : ""
                }
                onKeyDown={(e) => {
                  if (!isNaN(Number(e.key)) && e.key !== " ") {
                    e.preventDefault();
                  }
                }}
                onChange={(e) => {
                  const sanitized = e.target.value.replace(/[0-9]/g, "");
                  formik.setFieldValue("district", sanitized);
                  dispatch(
                    setLegacyField({ name: "district", value: sanitized }),
                  );
                }}
              />

              <Typography className="gray-700 font-medium">
                Pincode <span className="required">*</span>
              </Typography>
              <StyledTextField
                name="pincode"
                size="small"
                placeholder="Enter Pincode"
                type="number"
                onKeyDown={(e) => {
                  if (e.key === "-" || e.key === "e") {
                    e.preventDefault();
                  }
                }}
                sx={{ width: `${fieldWidth}px`, ...inputSX }}
                value={formik.values.pincode || ""}
                onBlur={formik.handleBlur}
                error={formik.touched.pincode && Boolean(formik.errors.pincode)}
                helperText={
                  formik.touched.pincode
                    ? String(formik.errors.pincode || "")
                    : ""
                }
                onChange={(e) => {
                  const value = e.target.value;
                  if (value === "" || Number(value) >= 0) {
                    formik.setFieldValue("pincode", value);
                    dispatch(setLegacyField({ name: "pincode", value: value }));
                  }
                }}
              />

              <Typography className="gray-700 font-medium">
                Highway Category <span className="required">*</span>
              </Typography>
              <StyledTextField
                name="highway_category"
                select
                size="small"
                sx={{ width: `${fieldWidth}px`, ...inputSX }}
                value={formik.values.highway_category || ""}
                onChange={(e) => {
                  formik.setFieldValue("highway_category", e.target.value);
                  dispatch(
                    setLegacyField({
                      name: "highway_category",
                      value: e.target.value,
                    }),
                  );
                }}
                onBlur={formik.handleBlur}
                error={
                  formik.touched.highway_category &&
                  Boolean(formik.errors.highway_category)
                }
                helperText={
                  formik.touched.highway_category
                    ? String(formik.errors.highway_category || "")
                    : ""
                }
              >
                <MenuItem value="">Select Highway Category</MenuItem>
                <MenuItem value="2-Lane">2-Lane</MenuItem>
                <MenuItem value="4-Lane">4-Lane</MenuItem>
                <MenuItem value="6-Lane">6-Lane</MenuItem>
                <MenuItem value="8-Lane">8-Lane</MenuItem>
                <MenuItem value="IL / SL">IL / SL</MenuItem>
              </StyledTextField>

              <Typography className="gray-700 font-medium">
                National Highway Number <span className="required">*</span>
              </Typography>
              <StyledTextField
                name="national_highway_number"
                size="small"
                placeholder="Enter National Highway Number"
                sx={{ width: `${fieldWidth}px`, ...inputSX }}
                value={formik.values.national_highway_number || ""}
                onChange={(e) => {
                  formik.setFieldValue(
                    "national_highway_number",
                    e.target.value,
                  );
                  dispatch(
                    setLegacyField({
                      name: "national_highway_number",
                      value: e.target.value,
                    }),
                  );
                }}
                onBlur={formik.handleBlur}
                error={
                  formik.touched.national_highway_number &&
                  Boolean(formik.errors.national_highway_number)
                }
                helperText={
                  formik.touched.national_highway_number
                    ? String(formik.errors.national_highway_number || "")
                    : ""
                }
              />

              <Typography className="gray-700 font-medium">
                Highway Side <span className="required">*</span>
              </Typography>
              <StyledTextField
                select
                size="small"
                sx={{ width: `${fieldWidth}px`, ...inputSX }}
                value={formik.values.highway_side || ""}
                onChange={(e) => {
                  formik.setFieldValue("highway_side", e.target.value);
                  dispatch(
                    setLegacyField({
                      name: "highway_side",
                      value: e.target.value,
                    }),
                  );
                }}
                onBlur={formik.handleBlur}
                error={
                  formik.touched.highway_side &&
                  Boolean(formik.errors.highway_side)
                }
                helperText={
                  formik.touched.highway_side
                    ? String(formik.errors.highway_side || "")
                    : ""
                }
              >
                <MenuItem value="">Select Highway Side</MenuItem>
                <MenuItem value="Left-Hand Side">Left-Hand Side</MenuItem>
                <MenuItem value="Right-Hand Side">Right-Hand Side</MenuItem>
                <MenuItem value="Both Side of the Highway">
                  Both Side of the Highway
                </MenuItem>
              </StyledTextField>
            </Box>

            <Typography fontSize={13} fontWeight={600} mb={1} mt={2}>
              Enter Following Required Details
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: `${labelWidth}px ${fieldWidth}px`,
                rowGap: "12px",
                columnGap: "32px",
                alignItems: "center",
                fontSize: "12px",
              }}
            >
              <Typography color="#6b7280">
                Type of Utility <span style={{ color: "red" }}>*</span>
              </Typography>
              <FormControl>
                <RadioGroup
                  row
                  name="utility_type"
                  value={formik.values.utility_type}
                  onChange={(e) => {
                    const selectedLabel = e.target.value;
                    const selectedOption = utilityTypeOptions.find(
                      (option) => option.label === selectedLabel,
                    );
                    const selectedCategoryId = selectedOption?.value || "";

                    formik.setFieldValue("utility_type", selectedLabel);
                    formik.setFieldValue("category_id", selectedCategoryId);
                    formik.setFieldValue("amenity", "");

                    dispatch(
                      setLegacyField({
                        name: "utility_type",
                        value: selectedLabel,
                      }),
                    );
                    dispatch(
                      setLegacyField({
                        name: "category_id",
                        value: selectedCategoryId,
                      }),
                    );
                    dispatch(
                      setLegacyField({
                        name: "amenity",
                        value: "",
                      }),
                    );

                    fetchAmenityOptions(selectedCategoryId);
                  }}
                  sx={{ flexWrap: "wrap" }}
                >
                  {utilityTypeOptions.map((option) => (
                    <FormControlLabel
                      key={String(option.value)}
                      value={option.label}
                      control={<Radio />}
                      label={option.label}
                    />
                  ))}
                </RadioGroup>
                {formik.touched.utility_type && formik.errors.utility_type ? (
                  <Typography color="error" fontSize={12} mt={0.5}>
                    {String(formik.errors.utility_type)}
                  </Typography>
                ) : null}
              </FormControl>

              <Typography color="#6b7280">
                Utility Category <span style={{ color: "red" }}>*</span>
              </Typography>
              <StyledTextField
                select
                size="small"
                sx={{ width: `${fieldWidth}px`, ...inputSX }}
                error={formik.touched.amenity && Boolean(formik.errors.amenity)}
                helperText={
                  formik.touched.amenity
                    ? String(formik.errors.amenity || "")
                    : ""
                }
                value={formik.values.amenity}
                onBlur={formik.handleBlur}
                onChange={(e) => {
                  formik.setFieldValue("amenity", e.target.value);
                  dispatch(
                    setLegacyField({ name: "amenity", value: e.target.value }),
                  );
                }}
              >
                <MenuItem value="">Select</MenuItem>
                {amenityOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </StyledTextField>

              <Typography color="#6b7280">
                Proposed Utility Alignment{" "}
                <span style={{ color: "red" }}>*</span>
              </Typography>
              <StyledTextField
                select
                size="small"
                sx={{ width: `${fieldWidth}px`, ...inputSX }}
                error={
                  formik.touched.proposed_utility_alignment &&
                  Boolean(formik.errors.proposed_utility_alignment)
                }
                helperText={
                  formik.touched.proposed_utility_alignment
                    ? String(formik.errors.proposed_utility_alignment || "")
                    : ""
                }
                {...formik.getFieldProps("proposed_utility_alignment")}
              >
                <MenuItem value="">Select</MenuItem>
                {proposedUtilityAlignmentOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </StyledTextField>

              <Typography color="#6b7280">
                Enter your Taluka <span style={{ color: "red" }}>*</span>
              </Typography>
              <StyledTextField
                size="small"
                placeholder="Enter Taluka"
                sx={{ width: `${fieldWidth}px`, ...inputSX }}
                {...formik.getFieldProps("taluka")}
                onChange={(e) => {
                  const sanitized = e.target.value.replace(/[^a-zA-Z0-9]/g, "");
                  formik.setFieldValue("taluka", sanitized);
                }}
                error={formik.touched.taluka && Boolean(formik.errors.taluka)}
                helperText={
                  formik.touched.taluka
                    ? String(formik.errors.taluka || "")
                    : ""
                }
              />

              <Typography color="#6b7280">
                Select Authority <span style={{ color: "red" }}>*</span>
              </Typography>
              <StyledTextField
                select
                name="authority"
                size="small"
                sx={{ width: `${fieldWidth}px`, ...inputSX }}
                value={selectedAuthority}
                onChange={(e) => handleAuthorityChange(String(e.target.value))}
                disabled={loadingAuthority}
              >
                <MenuItem value="">
                  {loadingAuthority ? "Loading..." : "Select Authority"}
                </MenuItem>

                {authorityOptions.map((option) => (
                  <MenuItem key={option.value} value={String(option.value)}>
                    {option.label}
                  </MenuItem>
                ))}
              </StyledTextField>

              <Typography color="#6b7280">
                Select Regional Office <span className="required">*</span>
              </Typography>
              <StyledTextField
                select
                name="ro"
                size="small"
                sx={{ width: `${fieldWidth}px`, ...inputSX }}
                value={selectedRo}
                onChange={(e) => handleRoChange(String(e.target.value))}
                disabled={!selectedAuthority || loadingRo}
                error={formik.touched.ro && Boolean(formik.errors.ro)}
                helperText={
                  formik.touched.ro ? String(formik.errors.ro || "") : ""
                }
              >
                <MenuItem value="">
                  {!selectedAuthority
                    ? "Select Authority First"
                    : loadingRo
                      ? "Loading..."
                      : "Select Regional Office"}
                </MenuItem>

                {roOptions.map((option) => (
                  <MenuItem key={option.value} value={String(option.value)}>
                    {option.label}
                  </MenuItem>
                ))}
              </StyledTextField>

              <Typography color="#6b7280">
                Select PIU (Multiple) <span style={{ color: "red" }}>*</span>
              </Typography>
              <Select
                ref={piuSelectRef}
                multiple
                name="piu_ids"
                size="small"
                sx={{ width: `${fieldWidth}px`, ...inputSX }}
                value={selectedPiu}
                open={piuSelectOpen}
                onOpen={() => setPiuSelectOpen(true)}
                onClose={() => setPiuSelectOpen(false)}
                onChange={(e) => {
                  const value =
                    typeof e.target.value === "string"
                      ? e.target.value.split(",")
                      : (e.target.value as string[]);

                  setSelectedPiu(value);
                  formik.setFieldValue("piu_ids", value);
                  setPiuSelectOpen(false);
                }}
                MenuProps={{
                  disableAutoFocus: true,
                  disableRestoreFocus: true,
                }}
                disabled={!selectedRo || loadingPiu}
                // onBlur={formik.handleBlur}
                renderValue={(selected) => (
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                    {(selected as string[]).map((value) => {
                      const option = piuOptions.find(
                        (opt) => String(opt.value) === String(value),
                      );
                      return (
                        <Chip
                          key={value}
                          label={option?.label || value}
                          size="small"
                          onMouseDown={(
                            e: React.MouseEvent<HTMLDivElement>,
                          ) => {
                            e.preventDefault();
                            e.stopPropagation();
                          }}
                          onDelete={(e: React.MouseEvent<HTMLDivElement>) => {
                            e.preventDefault();
                            e.stopPropagation();
                            const updatedPiu = selectedPiu.filter(
                              (piu) => piu !== value,
                            );
                            setSelectedPiu(updatedPiu);
                          }}
                        />
                      );
                    })}
                  </Box>
                )}
              >
                {!selectedRo ? (
                  <MenuItem disabled>Select RO First</MenuItem>
                ) : loadingPiu ? (
                  <MenuItem disabled>
                    <CircularProgress size={20} />
                  </MenuItem>
                ) : (
                  piuOptions.map((option) => (
                    <MenuItem key={option.value} value={String(option.value)}>
                      {option.label}
                    </MenuItem>
                  ))
                )}
              </Select>

              <Typography color="#6b7280">
                Number of Villages through which the proposed utility is passing{" "}
                <span style={{ color: "red" }}>*</span>
              </Typography>
              <StyledTextField
                name="villages_crossed_count"
                size="small"
                type="number"
                onKeyDown={(e) => {
                  if (e.key === "-" || e.key === "e") {
                    e.preventDefault();
                  }
                }}
                placeholder="Number of Villages through which the proposed utility is passing*"
                sx={{ width: `${fieldWidth}px`, ...inputSX }}
                value={formik.values.villages_crossed_count}
                onBlur={formik.handleBlur}
                error={
                  formik?.touched?.villages_crossed_count &&
                  Boolean(formik?.errors?.villages_crossed_count)
                }
                helperText={
                  formik?.touched?.villages_crossed_count
                    ? String(formik?.errors?.villages_crossed_count || "")
                    : ""
                }
                onChange={(e) => {
                  const value = e.target.value;
                  if (value === "" || Number(value) >= 0) {
                    formik.setFieldValue("villages_crossed_count", value);
                    const newCount = parseInt(value) || 0;
                    const trimmed = formik.values.villages_crossed_names.slice(
                      0,
                      newCount,
                    );
                    formik.setFieldValue("villages_crossed_names", trimmed);
                  }
                }}
              />

              <Typography color="#6b7280">
                Village Names <span className="required">*</span>
              </Typography>
              <Box sx={{ width: `${fieldWidth}px` }}>
                <StyledTextField
                  size="small"
                  placeholder={
                    parseInt(formik.values.villages_crossed_count as string) > 0
                      ? `Type village name and press Enter (${formik.values.villages_crossed_names.length}/${parseInt(formik.values.villages_crossed_count as string)})`
                      : "Enter village count first"
                  }
                  sx={{ width: "100%", ...inputSX }}
                  disabled={
                    !parseInt(formik.values.villages_crossed_count as string) ||
                    formik.values.villages_crossed_names.length >=
                    parseInt(formik.values.villages_crossed_count as string)
                  }
                  onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      const maxCount =
                        parseInt(
                          formik.values.villages_crossed_count as string,
                        ) || 0;
                      if (
                        formik.values.villages_crossed_names.length >= maxCount
                      )
                        return;
                      const val = (e.target as HTMLInputElement).value.trim();
                      if (!val) return;
                      formik.setFieldValue("villages_crossed_names", [
                        ...formik.values.villages_crossed_names,
                        val,
                      ]);
                      (e.target as HTMLInputElement).value = "";
                    }
                  }}
                  onKeyPress={(e: React.KeyboardEvent<HTMLInputElement>) => {
                    if (!isNaN(Number(e.key)) && e.key !== " ") {
                      e.preventDefault();
                    }
                  }}
                  onPaste={(e: React.ClipboardEvent<HTMLInputElement>) => {
                    const pastedText = e.clipboardData.getData("text");
                    if (/\d/.test(pastedText)) {
                      e.preventDefault();
                    }
                  }}
                />
                {formik.values.villages_crossed_names.length > 0 && (
                  <Box
                    sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: 1 }}
                  >
                    {formik.values.villages_crossed_names.map(
                      (name: string, i: number) => (
                        <Chip
                          key={i}
                          label={name}
                          size="small"
                          onDelete={() => {
                            const updated =
                              formik.values.villages_crossed_names.filter(
                                (_: string, idx: number) => idx !== i,
                              );
                            formik.setFieldValue(
                              "villages_crossed_names",
                              updated,
                            );
                          }}
                          sx={{ fontSize: 11, height: 24 }}
                        />
                      ),
                    )}
                  </Box>
                )}
              </Box>

              <Typography color="#6b7280">
                Number of Tehsils through which the proposed utility is passing{" "}
                <span style={{ color: "red" }}>*</span>
              </Typography>
              <StyledTextField
                name="tehsils_crossed_count"
                size="small"
                type="number"
                onKeyDown={(e) => {
                  if (e.key === "-" || e.key === "e") {
                    e.preventDefault();
                  }
                }}
                placeholder="Number of Tehsils through which the proposed utility is passing*"
                sx={{ width: `${fieldWidth}px`, ...inputSX }}
                value={formik.values.tehsils_crossed_count}
                onBlur={formik.handleBlur}
                error={
                  formik.touched.tehsils_crossed_count &&
                  Boolean(formik.errors.tehsils_crossed_count)
                }
                helperText={
                  formik.touched.tehsils_crossed_count
                    ? String(formik.errors.tehsils_crossed_count || "")
                    : ""
                }
                onChange={(e) => {
                  const value = e.target.value;
                  if (value === "" || Number(value) >= 0) {
                    formik.setFieldValue("tehsils_crossed_count", value);
                    const newCount = parseInt(value) || 0;
                    const trimmed = formik.values.tehsils_crossed_names.slice(
                      0,
                      newCount,
                    );
                    formik.setFieldValue("tehsils_crossed_names", trimmed);
                  }
                }}
              />

              <Typography color="#6b7280">
                Tehsil Names <span className="required">*</span>
              </Typography>
              <Box sx={{ width: `${fieldWidth}px` }}>
                <StyledTextField
                  size="small"
                  placeholder={
                    parseInt(formik.values.tehsils_crossed_count as string) > 0
                      ? `Type tehsil name and press Enter (${formik.values.tehsils_crossed_names.length}/${parseInt(formik.values.tehsils_crossed_count as string)})`
                      : "Enter tehsil count first"
                  }
                  sx={{ width: "100%", ...inputSX }}
                  disabled={
                    !parseInt(formik.values.tehsils_crossed_count as string) ||
                    formik.values.tehsils_crossed_names.length >=
                    parseInt(formik.values.tehsils_crossed_count as string)
                  }
                  onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      const maxCount =
                        parseInt(
                          formik.values.tehsils_crossed_count as string,
                        ) || 0;
                      if (
                        formik.values.tehsils_crossed_names.length >= maxCount
                      )
                        return;
                      const val = (e.target as HTMLInputElement).value.trim();
                      if (!val) return;
                      formik.setFieldValue("tehsils_crossed_names", [
                        ...formik.values.tehsils_crossed_names,
                        val,
                      ]);
                      (e.target as HTMLInputElement).value = "";
                    }
                  }}
                  onKeyPress={(e: React.KeyboardEvent<HTMLInputElement>) => {
                    if (!isNaN(Number(e.key)) && e.key !== " ") {
                      e.preventDefault();
                    }
                  }}
                  onPaste={(e: React.ClipboardEvent<HTMLInputElement>) => {
                    const pastedText = e.clipboardData.getData("text");
                    if (/\d/.test(pastedText)) {
                      e.preventDefault();
                    }
                  }}
                />
                {formik.values.tehsils_crossed_names.length > 0 && (
                  <Box
                    sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: 1 }}
                  >
                    {formik.values.tehsils_crossed_names.map(
                      (name: string, i: number) => (
                        <Chip
                          key={i}
                          label={name}
                          size="small"
                          onDelete={() => {
                            const updated =
                              formik.values.tehsils_crossed_names.filter(
                                (_: string, idx: number) => idx !== i,
                              );
                            formik.setFieldValue(
                              "tehsils_crossed_names",
                              updated,
                            );
                          }}
                          sx={{ fontSize: 11, height: 24 }}
                        />
                      ),
                    )}
                  </Box>
                )}
              </Box>

              {/* <Typography color="#6b7280">
                NH Land Area <span style={{ color: "red" }}>*</span>
              </Typography>
              <StyledTextField
                size="small"
                type="number"
                onKeyDown={(e) => {
                  if (e.key === "-" || e.key === "e") {
                    e.preventDefault();
                  }
                }}
                placeholder="NH Land Area"
                sx={{ width: `${fieldWidth}px`, ...inputSX }}
                value={formik.values.utilized_nh_land_area}
                onBlur={formik.handleBlur}
                onChange={(e) => {
                  const value = e.target.value;
                  if (value === "" || Number(value) >= 0) {
                    formik.setFieldValue("utilized_nh_land_area", value);
                    dispatch(
                      setLegacyField({
                        name: "utilized_nh_land_area",
                        value,
                      }),
                    );
                  }
                }}
                error={
                  formik.touched.utilized_nh_land_area &&
                  Boolean(formik.errors.utilized_nh_land_area)
                }
                helperText={
                  formik.touched.utilized_nh_land_area
                    ? String(formik.errors.utilized_nh_land_area || "")
                    : ""
                }
              /> */}

              <Typography color="#6b7280">
                Circle Rate <span style={{ color: "red" }}>*</span>
              </Typography>
              <StyledTextField
                size="small"
                type="number"
                onKeyDown={(e) => { 
                  if (e.key === "-" || e.key === "e" || e.key === "+" || (e.key === "0")) {
                    e.preventDefault();
                  }
                }}
                placeholder="Circle Rate"
                sx={{ width: `${fieldWidth}px`, ...inputSX }}
                {...formik.getFieldProps("circle_rate")}
                onChange={(e) => {
                  const value = e.target.value;
                  if (value === "" || Number(value) > 0) {
                    formik.setFieldValue("circle_rate", value);
                  }
                }}
              />


              <Typography color="#6b7280">
                Upload Circle Rate Certificate{" "}
                <span style={{ color: "red" }}>*</span>
              </Typography>
              <Box
                sx={{
                  background: "#eef2f7",
                  borderRadius: "6px",
                  px: 2,
                  py: 1,
                  display: "flex",
                  flexDirection: "column",
                  gap: 1,
                }}
              >
                <input
                  type="file"
                  id="circle-rate-upload"
                  style={{ display: "none" }}
                  accept=".pdf"
                  onChange={async (event) => {
                    const fileInput = event.target;
                    const file = fileInput.files?.[0];

                    try {
                      if (!file) return;

                      fileInput.value = "";

                      if (file.size > 21 * 1024 * 1024) {
                        toast.error("File size must be less than 20 MB");
                        return;
                      }

                      const fileUploadResponse = await dispatch(
                        genericUploadThunk({
                          file,
                          moduleType: "row",
                        }),
                      );

                      if (!fileUploadResponse?.payload?.success) {
                        const errorMsg =
                          fileUploadResponse?.payload?.response?.data
                            ?.details || fileUploadResponse?.payload?.details;

                        toast.error(
                          Array.isArray(errorMsg)
                            ? errorMsg[0]
                            : typeof errorMsg === "string"
                              ? errorMsg
                              : "Upload failed",
                        );
                        return;
                      }

                      if (!productionCheck) {
                        const uploadSuccess = await dispatch(
                          uploadToS3Thunk({
                            signedUrl:
                              fileUploadResponse.payload.data.signedUrl,
                            file,
                            contentType: file.type,
                          }),
                        ).unwrap();

                        if (!uploadSuccess) {
                          throw new Error("Upload to S3 failed");
                        }
                      }

                      const responseData = fileUploadResponse.payload.data;
                      setCircleRateDocument({
                        originalFileName: file.name,
                        fileName: responseData.fileName,
                        objectKey: responseData.objectKey,
                      });

                      toast.success("File uploaded successfully");
                    } catch (error) {
                      console.error("Error uploading file:", error);
                      toast.error("Failed to upload file");
                    }
                  }}
                />

                {circleRateDocument ? (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      background: "#fff",
                      p: 1,
                      borderRadius: "4px",
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: 11,
                        color: "#757575",
                        cursor: "pointer",
                        textDecoration: "underline",
                        maxWidth: "70%",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        "&:hover": {
                          color: "#3A1C73",
                        },
                      }}
                      onClick={async () => {
                        try {
                          const res = await dispatch(
                            getFileROWSignedURLThunk({
                              document_name: circleRateDocument?.file_name,
                            }),
                          );

                          const signedUrl = res.payload?.data?.signedUrl;

                          if (signedUrl) {
                            window.open(
                              signedUrl,
                              "_blank",
                              "noopener,noreferrer",
                            );
                          } else {
                            alert("Preview not available");
                          }
                        } catch (err) {
                          console.error("Preview error", err);
                        }
                      }}
                      title={circleRateDocument?.originalFileName}
                    >
                      {circleRateDocument?.originalFileName}
                    </Typography>

                    <Box display="flex" gap={0.5}>
                      <IconButton
                        size="small"
                        sx={{
                          background: "#FFFFFF",
                          borderRadius: "6px",
                          border: "1px solid #D0D5DD",
                          padding: "4px 6px",
                          "&:hover": {
                            backgroundColor: "#E7E4F8",
                          },
                        }}
                        onClick={async () => {
                          try {
                            const res = await dispatch(
                              getFileROWSignedURLThunk({
                                document_name: circleRateDocument.fileName,
                              }),
                            );

                            const signedUrl = res.payload?.data?.signedUrl;

                            if (signedUrl) {
                              window.open(
                                signedUrl,
                                "_blank",
                                "noopener,noreferrer",
                              );
                            } else {
                              alert("Preview URL not found");
                            }
                          } catch (err) {
                            console.error(err);
                          }
                        }}
                      >
                        <RemoveRedEyeOutlinedIcon fontSize="small" />
                      </IconButton>

                      <IconButton
                        size="small"
                        sx={{
                          background: "#FFFFFF",
                          borderRadius: "6px",
                          border: "1px solid #D0D5DD",
                          padding: "4px 6px",
                          "&:hover": {
                            backgroundColor: "#E7E4F8",
                          },
                        }}
                        onClick={async () => {
                          try {
                            const deleteRes = await dispatch(
                              getDeleteSignedURLThunk(
                                circleRateDocument?.fileName,
                              ),
                            );

                            const signedUrl =
                              deleteRes.payload?.data?.signedUrl;

                            if (!signedUrl) {
                              throw new Error("Delete URL not found");
                            }

                            await deleteFileOnAwsAPI(signedUrl);

                            setCircleRateDocument(null);

                            toast.success(
                              `${circleRateDocument?.originalFileName} deleted successfully`,
                            );
                          } catch (error) {
                            console.error("Delete error:", error);
                            toast.error("Delete failed");
                          }
                        }}
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </Box>
                ) : null}

                <Typography
                  component="label"
                  htmlFor="circle-rate-upload"
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                    fontSize: 11,
                    color: "#3a1c73",
                    cursor: "pointer",
                    fontWeight: 500,
                  }}
                >
                  <FileUploadOutlinedIcon fontSize="small" />
                  Upload Circle Rate Certificate
                </Typography>
                <Typography fontSize={10} color="#9ca3af">
                  (Supported Format: PDF only, Max 20MB per file)
                </Typography>
              </Box>

              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  mb: 1,
                  position: "relative",
                }}
              >
                <Typography color="#6b7280">
                  Please select the status of your existing application in the
                  legacy (morthnoc.nic.in) system to continue the applicable
                  process on Rajmarg Pravesh Portal:{" "}
                  <span className="required">*</span>
                </Typography>
              </Box>

              <FormControl sx={{ width: `${fieldWidth}px` }}>
                <StyledTextField
                  select
                  size="small"
                  name="license_type"
                  sx={{ width: `${fieldWidth}px`, ...inputSX }}
                  value={licenseTypeSelection || ""}
                  onChange={(e: any) => {
                    const selectedRadioValue = e.target.value;
                    const selectedLicenseType =
                      selectedRadioValue === "PROVISIONAL_APPROVED" ||
                        selectedRadioValue === "PROVISIONAL_UNDER_REVIEW"
                        ? "PROVISIONAL"
                        : selectedRadioValue;

                    setLicenseTypeSelection(selectedRadioValue);
                    formik.setFieldValue("license_type", selectedLicenseType);
                    if (selectedRadioValue !== "PROVISIONAL_UNDER_REVIEW") {
                      formik.setFieldValue("legacy_substage_id", "");

                      dispatch(
                        setLegacyFields({
                          legacy_substage_id: "",
                          legacy_substage_name: "",
                        }),
                      );
                    }

                    dispatch(
                      setLegacyFields({
                        license_type: selectedLicenseType,
                        license_type_selection: selectedRadioValue,
                      }),
                    );
                    formik.setFieldValue("final_license_issue_date", "");
                    formik.setFieldValue("final_expiry_date", "");
                    formik.setFieldError("final_license_issue_date", "");
                    formik.setFieldError("final_expiry_date", "");

                    formik.setFieldTouched("final_license_issue_date", false);
                    formik.setFieldTouched("final_expiry_date", false);

                    setUploadedFile(null);
                  }}
                >
                  <MenuItem value="">Select Application Status</MenuItem>
                  <MenuItem value="PROVISIONAL_APPROVED">
                    My application has already received In-Principle Approval /
                    Provisional Approval
                  </MenuItem>
                  <MenuItem value="PROVISIONAL_UNDER_REVIEW">
                    My application is under review for Provisional Approval and
                    is currently scrutiny, inspection, or payment is under
                    process
                  </MenuItem>
                  <MenuItem value="FINAL">
                    My application has already received Final Approval / Final
                    NOC
                  </MenuItem>
                </StyledTextField>
                {formik.touched.license_type && formik.errors.license_type ? (
                  <Typography color="error" fontSize={12} mt={0.5}>
                    {String(formik.errors.license_type)}
                  </Typography>
                ) : null}
              </FormControl>

              {filteredSubStageOptions.length > 0 && (
                <>
                  <Typography color="#6b7280">
                    Select Sub-stage <span style={{ color: "red" }}>*</span>
                  </Typography>
                  <FormControl sx={{ width: `${fieldWidth}px` }}>
                    <StyledTextField
                      select
                      size="small"
                      name="legacy_substage_id"
                      sx={{ width: `${fieldWidth}px`, ...inputSX }}
                      value={formik.values.legacy_substage_id || ""}
                      onChange={(e: any) => {
                        const selectedId = String(e.target.value);
                        const selectedSubStage = filteredSubStageOptions.find(
                          (item) => String(item.id) === selectedId,
                        );

                        formik.setFieldValue("legacy_substage_id", selectedId);
                        dispatch(
                          setLegacyFields({
                            legacy_substage_id: selectedId,
                            legacy_substage_name: selectedSubStage?.name || "",
                          }),
                        );
                      }}
                    >
                      <MenuItem value="">Select Sub-stage</MenuItem>
                      {filteredSubStageOptions.map((item) => (
                        <MenuItem key={item.id} value={String(item.id)}>
                          {item.name}
                        </MenuItem>
                      ))}
                    </StyledTextField>
                    {formik.touched.legacy_substage_id &&
                      formik.errors.legacy_substage_id ? (
                      <Typography color="error" fontSize={12} mt={0.5}>
                        {String(formik.errors.legacy_substage_id)}
                      </Typography>
                    ) : null}
                  </FormControl>
                </>
              )}
            </Box>
            {/*  */}
            {shouldShowLicenseDocumentForType(effectiveLicenseType) && (
              <Box mt={3}>
                <Typography fontSize={12} fontWeight={600} mb={1}>
                  {effectiveLicenseType === "PROVISIONAL"
                    ? "Upload Copy of Provisional NOC/ In principal approval"
                    : "Upload Copy of Final NOC/ Final approval"}{" "}
                  <span style={{ color: "red" }}>*</span>
                </Typography>
                <Divider sx={{ mb: 1.5 }} />

                <Box
                  sx={{
                    background: "#eef2f7",
                    borderRadius: "6px",
                    px: 2,
                    py: 1,
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <React.Fragment>
                    <input
                      type="file"
                      id="upload-provisional-license"
                      style={{ display: "none" }}
                      accept=".pdf"
                      onChange={async (event) => {
                        const fileInput = event.target;
                        const file = fileInput.files?.[0];

                        try {
                          if (!file) return;

                          fileInput.value = "";

                          if (file.size > 21 * 1024 * 1024) {
                            toast.error("File size must be less than 20 MB");
                            return;
                          }

                          const fileUploadURL = await dispatch(
                            genericUploadThunk({
                              file,
                              moduleType: "row",
                            }),
                          );

                          if (!fileUploadURL?.payload?.success) {
                            const errorMsg =
                              fileUploadURL?.payload?.response?.data?.details ||
                              fileUploadURL?.payload?.details;

                            toast.error(
                              Array.isArray(errorMsg)
                                ? errorMsg[0]
                                : typeof errorMsg === "string"
                                  ? errorMsg
                                  : "Something went wrong!",
                            );
                            return;
                          }

                          if (!productionCheck) {
                            const uploadSuccess = await dispatch(
                              uploadToS3Thunk({
                                signedUrl: fileUploadURL.payload.data.signedUrl,
                                file,
                                contentType: file.type,
                              }),
                            ).unwrap();

                            if (!uploadSuccess) {
                              throw new Error("File upload failed");
                            }
                          }
                          setUploadedFile({
                            originalFileName: file.name,
                            ...(effectiveLicenseType === "PROVISIONAL"
                              ? {
                                provisional_license_url:
                                  fileUploadURL.payload.data.objectKey,
                                provisional_license_name:
                                  fileUploadURL?.payload?.data?.fileName,
                                objectKeyProvisionalLicense:
                                  fileUploadURL.payload.data.objectKey,
                              }
                              : {
                                final_license_url:
                                  fileUploadURL.payload.data.objectKey,
                                final_license_name:
                                  fileUploadURL?.payload?.data?.fileName,
                                objectKeyFinalLicense:
                                  fileUploadURL.payload.data.objectKey,
                              }),
                          });

                          toast.success("File uploaded successfully");

                          formik.setFieldValue("provisionalFile", file.name);
                        } catch (error) {
                          console.error(error);
                          toast.error("Upload failed");
                        }
                      }}
                    />

                    <Box
                      sx={{
                        background: "#eef2f7",
                        borderRadius: "6px",
                        px: 2,
                        py: 1,
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        width: "100%",
                      }}
                    >
                      <Box>
                        <Typography fontSize={11}>
                          {effectiveLicenseType === "PROVISIONAL"
                            ? "Upload Provisional License"
                            : "Upload Final License"}
                        </Typography>
                        <Typography fontSize={10} color="#9ca3af">
                          (Supported Format PDF only)
                        </Typography>
                      </Box>

                      {uploadedFile ? (
                        <Box display="flex" alignItems="center" gap={1}>
                          <Typography
                            sx={{
                              fontSize: 11,
                              color: "#757575",
                              cursor: "pointer",
                              textDecoration: "underline",
                              "&:hover": {
                                color: "#3A1C73",
                              },
                            }}
                            onClick={async () => {
                              try {
                                const res = await dispatch(
                                  getFileROWSignedURLThunk({
                                    document_name:
                                      uploadedFile.provisional_license_url ||
                                      uploadedFile.final_license_url,
                                  }),
                                );

                                const signedUrl = res.payload?.data?.signedUrl;

                                if (signedUrl) {
                                  window.open(
                                    signedUrl,
                                    "_blank",
                                    "noopener,noreferrer",
                                  );
                                } else {
                                  alert("Preview not available");
                                }
                              } catch (err) {
                                console.error("Preview error", err);
                              }
                            }}
                            title={uploadedFile?.originalFileName}
                          >
                            {uploadedFile?.originalFileName?.length > 20
                              ? uploadedFile?.originalFileName?.slice(0, 20) +
                              "..."
                              : uploadedFile?.originalFileName}
                          </Typography>

                          <IconButton
                            sx={{
                              background: "#FFFFFF",
                              borderRadius: "8px",
                              border: "1px solid #D0D5DD",
                              padding: "6px 8px",
                              "&:hover": {
                                backgroundColor: "#E7E4F8",
                              },
                            }}
                          >
                            <RemoveRedEyeOutlinedIcon
                              fontSize="small"
                              onClick={async () => {
                                try {
                                  const res = await dispatch(
                                    getFileROWSignedURLThunk({
                                      document_name:
                                        uploadedFile.provisional_license_url ||
                                        uploadedFile.final_license_url,
                                    }),
                                  );

                                  const signedUrl =
                                    res.payload?.data?.signedUrl;

                                  if (signedUrl) {
                                    window.open(
                                      signedUrl,
                                      "_blank",
                                      "noopener,noreferrer",
                                    );
                                  } else {
                                    alert("Preview URL not found");
                                  }
                                } catch (err) {
                                  console.error(err);
                                }
                              }}
                            />
                          </IconButton>

                          <IconButton
                            sx={{
                              background: "#FFFFFF",
                              borderRadius: "8px",
                              border: "1px solid #D0D5DD",
                              padding: "6px 8px",
                              "&:hover": {
                                backgroundColor: "#E7E4F8",
                              },
                            }}
                          >
                            <CloseIcon
                              fontSize="small"
                              onClick={async () => {
                                try {
                                  const deleteRes = await dispatch(
                                    getDeleteSignedURLThunk(
                                      uploadedFile?.provisional_license_url ||
                                      uploadedFile?.final_license_url,
                                    ),
                                  );
                                  const signedUrl =
                                    deleteRes?.payload?.data?.signedUrl;
                                  if (!signedUrl) {
                                    throw new Error("Delete URL not found");
                                  }

                                  await deleteFileOnAwsAPI(signedUrl);

                                  setUploadedFile(null);
                                } catch (error) {
                                  console.error("Delete error:", error);
                                }
                              }}
                            />
                          </IconButton>
                        </Box>
                      ) : (
                        <Typography
                          component="label"
                          htmlFor="upload-provisional-license"
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                            fontSize: 11,
                            color: "#3a1c73",
                            cursor: "pointer",
                          }}
                        >
                          <UploadFileIcon fontSize="small" />
                          Upload File
                        </Typography>
                      )}
                    </Box>
                  </React.Fragment>
                </Box>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: `${labelWidth}px ${fieldWidth}px`,
                    rowGap: "12px",
                    columnGap: "32px",
                    alignItems: "center",
                    fontSize: "12px",
                  }}
                >
                  <Typography color="#6b7280">
                    {effectiveLicenseType === "PROVISIONAL"
                      ? "Provisional License Issue Date"
                      : "Final License Issue Date"}{" "}
                    <span style={{ color: "red" }}>*</span>
                  </Typography>
                  <StyledTextField
                    fullWidth
                    type="date"
                    size="small"
                    sx={{ mt: 1.5, width: "100%", ...inputSX }}
                    error={
                      formik.touched.final_license_issue_date &&
                      Boolean(formik.errors.final_license_issue_date)
                    }
                    helperText={
                      formik.touched.final_license_issue_date
                        ? String(formik.errors.final_license_issue_date || "")
                        : ""
                    }
                    {...formik.getFieldProps("final_license_issue_date")}
                  />

                  <Typography color="#6b7280">
                    {effectiveLicenseType === "PROVISIONAL"
                      ? "Provisional Expiry Date"
                      : "Final Expiry Date"}{" "}
                    <span style={{ color: "red" }}>*</span>
                  </Typography>
                  <StyledTextField
                    fullWidth
                    type="date"
                    InputLabelProps={{ shrink: true }}
                    sx={{ mt: 1.5, width: "100%", ...inputSX }}
                    error={
                      formik.touched.final_expiry_date &&
                      Boolean(formik.errors.final_expiry_date)
                    }
                    helperText={
                      formik.touched.final_expiry_date
                        ? String(formik.errors.final_expiry_date || "")
                        : ""
                    }
                    {...formik.getFieldProps("final_expiry_date")}
                  />
                </Box>
                {isExpired && (
                  <Box
                    sx={{
                      mt: 2,
                      p: 1.5,
                      borderRadius: "8px",
                      backgroundColor: "#FFF8E1",
                      border: "1px solid #FFE082",
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    <InfoIcon
                      sx={{
                        color: "#8A6D3B",
                        fontSize: 18,
                        flexShrink: 0,
                      }}
                    />

                    <Typography
                      sx={{
                        fontSize: "12px",
                        color: "#8A6D3B",
                        lineHeight: 1.5,
                      }}
                    >
                      If an extension to the validity of the{" "}
                      {effectiveLicenseType === "PROVISIONAL"
                        ? "Provisional NOC"
                        : "Final NOC"}{" "}
                      has been granted, the revised expiry date may be
                      mentioned.
                    </Typography>
                  </Box>
                )}
              </Box>
            )}
            <Box mt={3}>
              <Box
                sx={{
                  background: "#eef2f7",
                  borderRadius: "6px",
                  p: 2,
                }}
              >
                <Typography fontSize={12} fontWeight={600} mb={1}>
                  Pin and Enter Start and End Chainage on GIS Map Chainage
                </Typography>
                {isAcross &&
                  formik.submitCount > 0 &&
                  acrossChainages.length === 0 && (
                    <Typography color="error" fontSize={12} mt={1}>
                      Please add at least one Crossing.
                    </Typography>
                  )}

                {isAlong &&
                  formik.submitCount > 0 &&
                  alongChainages.length === 0 && (
                    <Typography color="error" fontSize={12} mt={1}>
                      Please add at least one Along Chainage.
                    </Typography>
                  )}
                <Box
                  sx={{
                    display: "flex",
                    gap: 3,
                    mb: 2,
                    flexWrap: "wrap",
                  }}
                >
                  <Box>
                    <Typography fontSize={11} mb={0.5}>
                      Start Chainage <span className="required">*</span>
                    </Typography>

                    <Box display="flex" gap={1} alignItems="center">
                      <StyledTextField
                        size="small"
                        placeholder="Km"
                        type="number"
                        onKeyDown={(e) => {
                          if (e.key === "-" || e.key === "e") {
                            e.preventDefault();
                          }
                        }}
                        sx={{ width: 200, ...inputSX }}
                        error={
                          formik.touched.startKm &&
                          Boolean(formik.errors.startKm)
                        }
                        helperText={
                          formik.touched.startKm
                            ? String(formik.errors.startKm || "")
                            : ""
                        }
                        value={formik.values.startKm}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (value === "" || Number(value) >= 0) {
                            formik.setFieldValue("startKm", value);
                          }
                        }}
                      />
                      +
                      <StyledTextField
                        size="small"
                        placeholder="m"
                        type="number"
                        onKeyDown={(e) => {
                          if (e.key === "-" || e.key === "e") {
                            e.preventDefault();
                          }
                        }}
                        sx={{ width: 200, ...inputSX }}
                        error={
                          formik.touched.startM && Boolean(formik.errors.startM)
                        }
                        helperText={
                          formik.touched.startM
                            ? String(formik.errors.startM || "")
                            : ""
                        }
                        value={formik.values.startM}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (value === "" || Number(value) >= 0) {
                            formik.setFieldValue("startM", value);
                          }
                        }}
                      />
                    </Box>
                  </Box>

                  <Box>
                    <Typography fontSize={11} mb={0.5}>
                      End Chainage <span className="required">*</span>
                    </Typography>

                    <Box display="flex" gap={1} alignItems="center">
                      <StyledTextField
                        size="small"
                        placeholder="Km"
                        type="number"
                        onKeyDown={(e) => {
                          if (e.key === "-" || e.key === "e") {
                            e.preventDefault();
                          }
                        }}
                        sx={{ width: 200, ...inputSX }}
                        error={
                          formik.touched.endKm && Boolean(formik.errors.endKm)
                        }
                        helperText={
                          formik.touched.endKm
                            ? String(formik.errors.endKm || "")
                            : ""
                        }
                        value={formik.values.endKm}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (value === "" || Number(value) >= 0) {
                            formik.setFieldValue("endKm", value);
                          }
                        }}
                      />
                      +
                      <StyledTextField
                        size="small"
                        placeholder="m"
                        type="number"
                        onKeyDown={(e) => {
                          if (e.key === "-" || e.key === "e") {
                            e.preventDefault();
                          }
                        }}
                        sx={{ width: 200, ...inputSX }}
                        error={
                          formik.touched.endM && Boolean(formik.errors.endM)
                        }
                        helperText={
                          formik.touched.endM
                            ? String(formik.errors.endM || "")
                            : ""
                        }
                        value={formik.values.endM}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (value === "" || Number(value) >= 0) {
                            formik.setFieldValue("endM", value);
                          }
                        }}
                      />
                    </Box>
                  </Box>
                </Box>
                <Box mt={2}>
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: {
                        xs: "1fr",
                        lg:
                          isAlong && isAcross
                            ? "repeat(2, minmax(0, 1fr))"
                            : "1fr",
                      },
                      gap: 3,
                      alignItems: "start",
                    }}
                  >
                    {isAlong && (
                      <Box
                        sx={{
                          border: "1px solid #E4E7EC",
                          borderRadius: 2,
                          p: 2,
                          bgcolor: "#fff",
                          height: "100%",
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: 16,
                            fontWeight: 600,
                            color: "#101828",
                            mb: 2,
                          }}
                        >
                          Along Side Chainage Details
                        </Typography>

                        <ChainageSection
                          mode="chainage"
                          chainages={alongChainages}
                          setChainages={(value) =>
                            dispatch(
                              setAlongChainages(
                                typeof value === "function"
                                  ? value(alongChainages)
                                  : value,
                              ),
                            )
                          }
                          formik={formik}
                        />
                      </Box>
                    )}

                    {isAcross && (
                      <Box
                        sx={{
                          border: "1px solid #E4E7EC",
                          borderRadius: 2,
                          p: 2,
                          bgcolor: "#fff",
                          height: "100%",
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: 16,
                            fontWeight: 600,
                            color: "#101828",
                            mb: 2,
                          }}
                        >
                          Across Chainage Details
                        </Typography>

                        <ChainageSection
                          mode="crossing"
                          chainages={acrossChainages}
                          setChainages={(value) =>
                            dispatch(
                              setAcrossChainages(
                                typeof value === "function"
                                  ? value(acrossChainages)
                                  : value,
                              ),
                            )
                          }
                          formik={formik}
                        />
                      </Box>
                    )}
                  </Box>
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
      </form>
      <div style={{ width: "40rem", padding: "0 1rem" }}>
        <MapSearchInput />
      </div>
      <MapPanel
        hideRemovePin={false}
        startChainage={`${formik.values.startKm || 0}+${formik.values.startM || 0}`}
        endChainage={`${formik.values.endKm || 0}+${formik.values.endM || 0}`}
        geoCoordinates={geoCoordinates}
        start_coordinates={start_coordinates}
        end_coordinates={end_coordinates}
        onMapClick={handleMapClick}
        isFirstSectionCompletedRow={true}
      />
    </>
  );
};
export default LegacyAmenityDetailsRow;
